import { ReadingAssessmentTestResult, ReadingAssessmentDerivedMetrics } from "types/tests";
import { calculateAgeForTest } from "utils/CommonUtil";

export interface GaugeZone {
  color: "green" | "amber" | "red";
  min: number;
  max: number;
}

export interface RiskMetric {
  label: string;
  value: string;
  expectedRange: string;
  points: number;
  status: "green" | "amber" | "red";
  rationale: string;
  normReference: string;
  gauge: {
    rawValue: number;
    zones: GaugeZone[];
  };
  percentile: MetricPercentile;
}

export interface MetricPercentile {
  percentile: number;
  zScore: number;
  populationMean: number;
  populationSd: number;
  direction: "higher-is-better" | "lower-is-better";
}

export interface ReadingRiskResult {
  totalScore: number;
  riskLabel: string;
  riskColor: string;
  confidence: "normal" | "low" | "none";
  metrics: {
    fixations: RiskMetric;
    regressions: RiskMetric;
    speed: RiskMetric;
    duration: RiskMetric;
  };
  comprehension: {
    correct: number;
    total: number;
    percentage: number;
  };
  ageBand: "7-12" | "13-17" | "18+" | "under-7";
  isInconclusive: boolean;
  speedPercentile: MetricPercentile;
  fixationSummary: {
    totalFixations: number;
    normalFixationsCount: number;
    normalFixationsPercentage: number;
    excessiveFixationsCount: number;
    excessiveFixationsPercentage: number;
  };
}

const SPEED_NORMS: Record<string, { mean: number; sd: number }> = {
  "7-12": { mean: 191, sd: 65 },
  "13-17": { mean: 170, sd: 50 },
  "18+": { mean: 238, sd: 63 },
  "under-7": { mean: 150, sd: 50 },
};

export function normalCdf(z: number): number {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + p * x);
  const erf = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1 + sign * erf);
}

function computeMetricPercentile(
  value: number,
  norm: { mean: number; sd: number },
  direction: MetricPercentile["direction"],
): MetricPercentile {
  const safeSd = norm.sd > 0 ? norm.sd : 1;
  const zScore = (value - norm.mean) / safeSd;
  const rawCdf = normalCdf(zScore);
  const performanceCdf = direction === "higher-is-better" ? rawCdf : 1 - rawCdf;
  const percentile = Math.max(1, Math.min(99, Math.round(performanceCdf * 100)));
  return {
    percentile,
    zScore,
    populationMean: norm.mean,
    populationSd: safeSd,
    direction,
  };
}

function computeSpeedPercentile(wpm: number, ageBand: string): MetricPercentile {
  const norm = SPEED_NORMS[ageBand] ?? SPEED_NORMS["18+"];
  return computeMetricPercentile(wpm, norm, "higher-is-better");
}

function deriveHigherIsWorseNorm(
  amberStart: number,
  redStart: number,
): { mean: number; sd: number } {
  // Threshold-derived normal model: amber starts near the 84th raw percentile,
  // red starts near the 95th raw percentile.
  const zAmber = 0.994;
  const zRed = 1.645;
  const sd = Math.max((redStart - amberStart) / (zRed - zAmber), 1);
  const mean = Math.max(0, amberStart - zAmber * sd);
  return { mean, sd };
}

export function calculateReadingRisk(
  testResult: ReadingAssessmentTestResult | undefined,
): ReadingRiskResult | null {
  if (!testResult || !testResult.results || !testResult.results.metrics) {
    return null;
  }

  const { results, startDate, patient } = testResult;
  const metrics = results.metrics as ReadingAssessmentDerivedMetrics;
  const summary = metrics.summary;

  // 1. Calculate Age and Band
  const dob = patient?.dob ? new Date(patient.dob) : new Date();
  const testDate = startDate ? new Date(startDate) : new Date();
  const age = calculateAgeForTest(testDate, dob);

  let ageBand: "7-12" | "13-17" | "18+" | "under-7" = "18+";
  if (age < 7) ageBand = "under-7";
  else if (age <= 12) ageBand = "7-12";
  else if (age <= 17) ageBand = "13-17";

  // 2. Normalization
  const progressedWordCount = summary.progressedWordCount || 1;
  const fixationsPer100Words = (summary.totalFixations / progressedWordCount) * 100;

  // Count backward saccades from all pages
  let totalBackwardSaccades = 0;
  metrics.pageMetrics?.forEach((pm) => {
    pm.fixationRecords?.forEach((fr) => {
      if (fr.transitionDirection === "backward") {
        totalBackwardSaccades++;
      }
    });
  });
  const backwardSaccadesPer100Words = (totalBackwardSaccades / progressedWordCount) * 100;

  const wpm = summary.wordsPerMinute || 0;
  const meanFixationDurationMs = summary.averageFixationDuration * 1000;
  const totalFixations = summary.totalFixations || 0;
  const excessiveFixationsCount = summary.excessiveFixationsCount ?? 0;
  const normalFixationsCount =
    summary.normalFixationsCount ?? Math.max(0, totalFixations - excessiveFixationsCount);
  const excessiveFixationsPercentage =
    summary.excessiveFixationsPercentage ??
    (totalFixations > 0 ? (excessiveFixationsCount / totalFixations) * 100 : 0);
  const normalFixationsPercentage =
    summary.normalFixationsPercentage ??
    (totalFixations > 0 ? (normalFixationsCount / totalFixations) * 100 : 0);

  // Language check for backward saccades adjustment
  const isEnglishOrFrench =
    results.textListType?.toLowerCase().includes("eng") ||
    results.textListType?.toLowerCase().includes("fra");
  const backwardAdjustment = isEnglishOrFrench ? 3 : 0;

  // 3. Comprehension Gate
  const answers = results.comprehensionAnswers || [];
  const compTotal = answers.length;
  const compCorrect = answers.filter((a) => a.isCorrect).length;
  const compPercentage = compTotal > 0 ? (compCorrect / compTotal) * 100 : 0;

  let confidence: "normal" | "low" | "none" = "normal";
  let isInconclusive = false;

  if (compTotal > 0) {
    if (compPercentage <= 50) {
      confidence = "none";
      isInconclusive = true;
    } else if (compPercentage < 100) {
      confidence = "low";
    }
  }

  // 4. Point Scoring Logic
  // A. Fixations per 100 words
  let fixationPoints = 0;
  if (fixationsPer100Words > 190) fixationPoints = 2;
  else if (fixationsPer100Words >= 160) fixationPoints = 1;

  // B. Backward saccades per 100 words (with language adjustment)
  let regressionPoints = 0;
  const regAmber = 25 + backwardAdjustment;
  const regRed = 35 + backwardAdjustment;
  if (backwardSaccadesPer100Words > regRed) regressionPoints = 2;
  else if (backwardSaccadesPer100Words >= regAmber) regressionPoints = 1;

  // C. Words per minute (Silent reading provisional bands)
  let speedPoints = 0;
  if (ageBand === "7-12") {
    if (wpm < 125) speedPoints = 2;
    else if (wpm < 150) speedPoints = 1;
  } else if (ageBand === "13-17") {
    if (wpm < 120) speedPoints = 2;
    else if (wpm < 150) speedPoints = 1;
  } else {
    // 18+
    if (wpm < 140) speedPoints = 2;
    else if (wpm < 175) speedPoints = 1;
  }

  // D. Fixation duration
  let durationModifier = 0;
  let durationStatus: "green" | "amber" | "red" = "green";
  if (ageBand === "7-12") {
    if (meanFixationDurationMs > 380) {
      durationModifier = 1;
      durationStatus = "red";
    } else if (meanFixationDurationMs >= 330) durationStatus = "amber";
  } else if (ageBand === "13-17") {
    if (meanFixationDurationMs > 290) {
      durationModifier = 1;
      durationStatus = "red";
    } else if (meanFixationDurationMs >= 240) durationStatus = "amber";
  } else {
    // 18+
    if (meanFixationDurationMs > 300) {
      durationModifier = 1;
      durationStatus = "red";
    } else if (meanFixationDurationMs >= 250) durationStatus = "amber";
  }

  // 5. Final Score and Label
  const primaryScore = fixationPoints + regressionPoints + speedPoints;
  let totalScore = primaryScore + durationModifier;

  // Override rules
  if (primaryScore <= 1 && durationStatus === "red") {
    totalScore = Math.min(totalScore, 3);
  }
  if (
    (fixationPoints === 2 && regressionPoints === 2) ||
    (fixationPoints === 2 && speedPoints === 2) ||
    (regressionPoints === 2 && speedPoints === 2)
  ) {
    if (totalScore < 4) totalScore = 4; // At least Moderate if two primary metrics are red
  }

  let riskLabel = "Reading Ability Within Expected Range";
  let riskColor = "green";
  if (totalScore >= 7) {
    riskLabel = "High Risk of Reading Disturbance";
    riskColor = "red";
  } else if (totalScore >= 5) {
    riskLabel = "Monitor Reading Ability";
    riskColor = "yellow";
  }

  if (isInconclusive) {
    riskLabel = "Inconclusive — Insufficient Reading Validity";
    riskColor = "gray";
  }

  const getStatus = (pts: number) => (pts === 2 ? "red" : pts === 1 ? "amber" : "green");

  function higherIsWorseZones(
    rawValue: number,
    amberStart: number,
    redStart: number,
  ): { rawValue: number; zones: GaugeZone[] } {
    const rangeMax = Math.max(rawValue * 1.2, redStart * 1.5);
    return {
      rawValue,
      zones: [
        { color: "green", min: 0, max: amberStart },
        { color: "amber", min: amberStart, max: redStart },
        { color: "red", min: redStart, max: rangeMax },
      ],
    };
  }

  function lowerIsWorseZones(
    rawValue: number,
    amberEnd: number,
    redEnd: number,
  ): { rawValue: number; zones: GaugeZone[] } {
    const rangeMax = Math.max(rawValue * 1.2, amberEnd * 1.5);
    return {
      rawValue,
      zones: [
        { color: "red", min: 0, max: redEnd },
        { color: "amber", min: redEnd, max: amberEnd },
        { color: "green", min: amberEnd, max: rangeMax },
      ],
    };
  }

  const speedAmberEnd = ageBand === "7-12" ? 150 : ageBand === "13-17" ? 150 : 175;
  const speedRedEnd = ageBand === "7-12" ? 125 : ageBand === "13-17" ? 120 : 140;

  const durationAmberStart = ageBand === "7-12" ? 330 : ageBand === "13-17" ? 240 : 250;
  const durationRedStart = ageBand === "7-12" ? 380 : ageBand === "13-17" ? 290 : 300;
  const speedPercentile = computeSpeedPercentile(wpm, ageBand);
  const fixationNorm = deriveHigherIsWorseNorm(160, 190);
  const regressionNorm = deriveHigherIsWorseNorm(regAmber, regRed);
  const durationNorm = deriveHigherIsWorseNorm(durationAmberStart, durationRedStart);

  return {
    totalScore,
    riskLabel,
    riskColor,
    confidence,
    isInconclusive,
    ageBand,
    speedPercentile,
    fixationSummary: {
      totalFixations,
      normalFixationsCount,
      normalFixationsPercentage,
      excessiveFixationsCount,
      excessiveFixationsPercentage,
    },
    comprehension: {
      correct: compCorrect,
      total: compTotal,
      percentage: compPercentage,
    },
    metrics: {
      fixations: {
        label: "Fixation Frequency",
        value: `${Math.round(fixationsPer100Words)} per 100 words`,
        expectedRange: "<160 per 100 words",
        points: fixationPoints,
        status: getStatus(fixationPoints),
        normReference: "Expected: <160 per 100 words",
        rationale:
          "How often the eyes pause on words — more pauses can indicate greater decoding effort.",
        gauge: higherIsWorseZones(fixationsPer100Words, 160, 190),
        percentile: computeMetricPercentile(fixationsPer100Words, fixationNorm, "lower-is-better"),
      },
      regressions: {
        label: "Regression Rate",
        value: `${Math.round(backwardSaccadesPer100Words)} per 100 words`,
        expectedRange: `<${25 + backwardAdjustment} per 100 words`,
        points: regressionPoints,
        status: getStatus(regressionPoints),
        normReference: `Expected: <${25 + backwardAdjustment} per 100 words`,
        rationale:
          "How often the eyes jump backward to re-read — frequent regressions suggest difficulty maintaining comprehension or decoding.",
        gauge: higherIsWorseZones(backwardSaccadesPer100Words, regAmber, regRed),
        percentile: computeMetricPercentile(
          backwardSaccadesPer100Words,
          regressionNorm,
          "lower-is-better",
        ),
      },
      speed: {
        label: "Reading Speed",
        value: `${Math.round(wpm)} WPM`,
        expectedRange: `>${speedAmberEnd} WPM`,
        points: speedPoints,
        status: getStatus(speedPoints),
        normReference: `Expected: >${speedAmberEnd} WPM`,
        rationale:
          "Words read per minute — slower speeds may reflect effortful word-by-word processing.",
        gauge: lowerIsWorseZones(wpm, speedAmberEnd, speedRedEnd),
        percentile: speedPercentile,
      },
      duration: {
        label: "Fixation Duration",
        value: `${Math.round(meanFixationDurationMs)} ms`,
        expectedRange: `<${durationAmberStart} ms`,
        points: durationModifier,
        status: durationStatus,
        normReference:
          ageBand === "7-12"
            ? "Expected: <330 ms"
            : ageBand === "13-17"
              ? "Expected: <240 ms"
              : "Expected: <250 ms",
        rationale:
          "Average time spent on each pause — longer pauses can reflect difficulty processing individual words.",
        gauge: higherIsWorseZones(meanFixationDurationMs, durationAmberStart, durationRedStart),
        percentile: computeMetricPercentile(
          meanFixationDurationMs,
          durationNorm,
          "lower-is-better",
        ),
      },
    },
  };
}
